import { useMemo } from 'react'

import type { Table } from '@tanstack/react-table'

import LeftChevronIcon from '@/assets/icons/icon-chevron-left.svg'
import RightChevronIcon from '@/assets/icons/icon-chevron-right.svg'
import { PaginationTypes } from '@/components/ui/table/controlled-pagination/DataTable.tsx'
import { generatePageNumbers } from '@/components/ui/table/controlled-pagination/helpers.ts'
import { classNames } from '@/utils/helpers.ts'

import { Button } from '../../button.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../select.tsx'

interface IDataTablePaginationProps<TData> {
  table: Table<TData>
  className?: string
  type?: PaginationTypes
  disabled?: boolean
}

const paginationOptions = [10, 20, 30, 40, 50]

export const DataTablePagination = <TData,>({
  table,
  className,
  type,
  disabled,
}: IDataTablePaginationProps<TData>) => {
  const { pageSize, pageIndex: currentPage } = table.getState().pagination
  const totalPages = table.getPageCount()
  const displayedPages = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  )

  const handleSelectChange = (value: string) => {
    table.setPageSize(Number(value))
  }

  const handlePreviousButtonClick = () => {
    table.previousPage()
  }

  const handleNextButtonClick = () => {
    table.nextPage()
  }

  return (
    <div
      className={classNames(
        'mt-6 flex w-full flex-col items-center justify-between gap-2 sm:flex-row',
        className,
      )}
    >
      <div className="flex items-center gap-4 text-sm">
        {type === PaginationTypes.DEFAULT && totalPages > 0 && (
          <div>
            {pageSize * currentPage + 1} - {pageSize * (currentPage + 1)} of{' '}
            {totalPages * pageSize}
          </div>
        )}

        {type === PaginationTypes.SIZE_SELECT && (
          <>
            <div className="text-secondary-500 text-slate-900 dark:text-white">
              Rows per page
            </div>
            <Select
              onValueChange={handleSelectChange}
              value={`${pageSize}`}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent>
                {paginationOptions.map((pageSize) => (
                  <SelectItem
                    key={pageSize}
                    value={`${pageSize}`}
                    className="hover:bg-background text-center"
                  >
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-sm dark:text-white">
              Page {currentPage + 1}
              {table.getPageCount() ? ` of ${table.getPageCount()}` : ''}
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          disabled={disabled || !table.getCanPreviousPage()}
          variant={type === PaginationTypes.DEFAULT ? 'nav' : 'outline'}
          onClick={handlePreviousButtonClick}
        >
          <LeftChevronIcon className="fill-secondary-500" />
          Previous
        </Button>
        {type === PaginationTypes.DEFAULT && (
          <div className="flex gap-3">
            {displayedPages.map((page) => (
              <div
                className="h-5 min-w-5"
                key={page}
              >
                {page === -1 ? (
                  <span className="break-label">...</span>
                ) : (
                  <div
                    className={
                      page === currentPage + 1
                        ? 'min-w-5 cursor-pointer rounded bg-[#1E84E3] px-1 text-center font-normal'
                        : 'min-w-5 cursor-pointer text-center'
                    }
                    onClick={() => table.setPageIndex(page - 1)}
                  >
                    {page}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <Button
          variant={type === PaginationTypes.DEFAULT ? 'nav' : 'outline'}
          onClick={handleNextButtonClick}
          disabled={disabled || !table.getCanNextPage()}
        >
          Next
          <RightChevronIcon className="fill-secondary-500" />
        </Button>
      </div>
    </div>
  )
}
